const TOKEN_KEY = "neon-arena-auth";

export interface AuthState {
  token: string;
  username: string;
  expiresAt: number;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  kills: number;
  deaths: number;
  wins: number;
  matches: number;
}

export interface PlayerStats {
  kills: number;
  deaths: number;
  matches: number;
  wins: number;
  time_played_ms: number;
  vehicleStats: Array<{ vehicle_id: string; kills: number; deaths: number; matches: number; wins: number; time_played_ms: number }>;
}

/** vehicleId → slot → category → level */
export type OwnedUpgradesMap = Record<string, Record<string, Record<string, number>>>;

function wsToHttp(url: string): string {
  return url.replace(/^wss?:\/\//, (m) => (m === "wss://" ? "https://" : "http://"));
}

export class AuthClient {
  private state: AuthState | null = null;
  private credits = 0;
  private ownedUpgrades: OwnedUpgradesMap = {};

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as AuthState;
      if (s.expiresAt > Date.now()) this.state = s;
      else localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
  }

  private save(): void {
    if (this.state) localStorage.setItem(TOKEN_KEY, JSON.stringify(this.state));
    else localStorage.removeItem(TOKEN_KEY);
  }

  private parseProgressionFields(data: Record<string, unknown>): void {
    if (typeof data["credits"] === "number") this.credits = data["credits"];
    if (data["ownedUpgrades"] && typeof data["ownedUpgrades"] === "object") {
      this.ownedUpgrades = data["ownedUpgrades"] as OwnedUpgradesMap;
    }
  }

  get isLoggedIn(): boolean { return this.state !== null && this.state.expiresAt > Date.now(); }
  get username(): string | null { return this.isLoggedIn ? (this.state?.username ?? null) : null; }
  get token(): string | null { return this.isLoggedIn ? (this.state?.token ?? null) : null; }

  getCredits(): number { return this.credits; }

  getOwnedLevel(vehicleId: string, slot: string, category: string): number {
    return this.ownedUpgrades[vehicleId]?.[slot]?.[category] ?? 0;
  }

  async register(serverUrl: string, username: string, password: string): Promise<void> {
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json() as Record<string, unknown>;
    if (!res.ok) throw new Error(String(data["error"] ?? "Registration failed"));
    this.state = { token: String(data["token"]), username: String(data["username"]), expiresAt: Number(data["expiresAt"]) };
    this.parseProgressionFields(data);
    this.save();
  }

  async login(serverUrl: string, username: string, password: string): Promise<void> {
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json() as Record<string, unknown>;
    if (!res.ok) throw new Error(String(data["error"] ?? "Login failed"));
    this.state = { token: String(data["token"]), username: String(data["username"]), expiresAt: Number(data["expiresAt"]) };
    this.parseProgressionFields(data);
    this.save();
  }

  async logout(serverUrl: string): Promise<void> {
    if (this.state) {
      try {
        await fetch(`${wsToHttp(serverUrl)}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this.state.token}` },
        });
      } catch { /* best-effort */ }
    }
    this.state = null;
    this.credits = 0;
    this.ownedUpgrades = {};
    this.save();
  }

  async fetchMe(serverUrl: string): Promise<void> {
    if (!this.state) return;
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/me`, {
      headers: { Authorization: `Bearer ${this.state.token}` },
    });
    if (!res.ok) return;
    const data = await res.json() as Record<string, unknown>;
    this.parseProgressionFields(data);
  }

  async fetchStats(serverUrl: string): Promise<PlayerStats> {
    if (!this.state) throw new Error("Not logged in");
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/me`, {
      headers: { Authorization: `Bearer ${this.state.token}` },
    });
    if (!res.ok) throw new Error("Failed to fetch stats");
    const data = await res.json() as { stats: PlayerStats } & Record<string, unknown>;
    this.parseProgressionFields(data);
    return data.stats;
  }

  async fetchLeaderboard(serverUrl: string): Promise<LeaderboardEntry[]> {
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/leaderboard`);
    if (!res.ok) throw new Error("Failed to fetch leaderboard");
    const data = await res.json() as { leaderboard: LeaderboardEntry[] };
    return data.leaderboard;
  }

  async purchaseUpgrade(
    serverUrl: string,
    vehicleId: string,
    slot: string,
    category: string,
    level: number,
  ): Promise<{ ok: true; creditsRemaining: number } | { ok: false; error: string }> {
    if (!this.state) return { ok: false, error: "not_authenticated" };
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/purchase-upgrade`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${this.state.token}` },
      body: JSON.stringify({ vehicleId, slot, category, level }),
    });
    const data = await res.json() as Record<string, unknown>;
    if (!res.ok) return { ok: false, error: String(data["error"] ?? "purchase_failed") };
    this.credits = Number(data["creditsRemaining"]);
    // Update local owned map
    this.ownedUpgrades[vehicleId] ??= {};
    this.ownedUpgrades[vehicleId][slot] ??= {};
    this.ownedUpgrades[vehicleId][slot][category] = level;
    return { ok: true, creditsRemaining: this.credits };
  }

  async sellUpgrade(
    serverUrl: string,
    vehicleId: string,
    slot: string,
    category: string,
  ): Promise<{ ok: true; creditsRemaining: number } | { ok: false; error: string }> {
    if (!this.state) return { ok: false, error: "not_authenticated" };
    const res = await fetch(`${wsToHttp(serverUrl)}/auth/sell-upgrade`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${this.state.token}` },
      body: JSON.stringify({ vehicleId, slot, category }),
    });
    const data = await res.json() as Record<string, unknown>;
    if (!res.ok) return { ok: false, error: String(data["error"] ?? "sell_failed") };
    this.credits = Number(data["creditsRemaining"]);
    // Reduce local owned level
    const current = this.ownedUpgrades[vehicleId]?.[slot]?.[category] ?? 0;
    if (current > 1) {
      this.ownedUpgrades[vehicleId][slot][category] = current - 1;
    } else if (this.ownedUpgrades[vehicleId]?.[slot]) {
      delete this.ownedUpgrades[vehicleId][slot][category];
    }
    return { ok: true, creditsRemaining: this.credits };
  }
}
